import React, { useEffect, useRef, useState } from 'react';
import { claimSwipe, releaseSwipe } from '../utils/swipeOwner';
import EventIcon, { iconOf, stripIcon } from './EventIcon';
import { uses12h } from '../utils/timeFormat';
import { format, addDays, subDays, parseISO, isToday as fnsIsToday, isTomorrow as fnsIsTomorrow } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import EventCard from './EventCard';
import LabelsMenu from './LabelsMenu';
import CalendarsLimited from './CalendarsLimited';
import WeekView from './WeekView';
import MonthView from './MonthView';
import YearView from './YearView';
import SlotGridView from './SlotGridView';
import PearlCard from './PearlCard';
import { SpeakerIcon, StopIcon, PlannerIcon, ListIcon } from './icons';
import { useAuth } from '../context/AuthContext';
import { useSubCalendars } from '../context/SubCalendarContext';
import { useT, DATE_LOCALES } from '../i18n';
import { useWeather } from '../hooks/useWeather';
import { applyTTSPrefs, voicesReady } from '../utils/ttsUtils';
import { todayEventsCache, labelAgendaCache } from '../utils/viewCache';
import { readableText } from '../utils/color';

// Shown only when the day could not be read aloud at all — the browser has no
// voice of its own AND the server could not supply one. Short on purpose: it
// sits in a narrow row next to the button.
const TTS_FAIL = {
  it: 'Non riesco a leggerlo ad alta voce ora.',
  en: "Can't read it aloud right now.",
  es: 'Ahora no puedo leerlo en voz alta.',
  de: 'Kann es gerade nicht vorlesen.',
  fr: 'Impossible de le lire à voix haute pour le moment.',
  nl: 'Kan het nu niet voorlezen.',
  pt: 'Não consigo ler em voz alta agora.',
  ru: 'Сейчас не могу прочитать вслух.',
  pl: 'Nie mogę teraz odczytać na głos.',
  ro: 'Nu îl pot citi cu voce tare acum.',
  zh: '现在无法朗读。',
  ja: '今は読み上げられません。',
  hi: 'अभी इसे पढ़कर नहीं सुना सकता।',
  ko: '지금은 읽어 줄 수 없습니다.',
  ar: 'لا يمكنني قراءته بصوت عالٍ الآن.',
};

function langToSpeechLang(lang) {
  const map = {
    it: 'it-IT', en: 'en-US', 'en-US': 'en-US', 'en-GB': 'en-GB',
    es: 'es-ES', 'es-ES': 'es-ES', 'es-AR': 'es-AR', 'es-MX': 'es-MX', 'es-CO': 'es-CO',
    de: 'de-DE', fr: 'fr-FR', nl: 'nl-NL',
    pt: 'pt-PT', 'pt-PT': 'pt-PT', 'pt-BR': 'pt-BR',
    ru: 'ru-RU', pl: 'pl-PL', ro: 'ro-RO', zh: 'zh-CN', ja: 'ja-JP', hi: 'hi-IN', ko: 'ko-KR', ar: 'ar-SA',
  };
  return map[lang] || 'it-IT';
}

function formatEventTime(isoStr, timezone, lang) {
  try {
    const date = new Date(isoStr);
    const use12h = uses12h(lang);
    return date.toLocaleTimeString(lang || 'it-IT', {
      hour: '2-digit', minute: '2-digit',
      hour12: use12h,
      timeZone: timezone,
    });
  } catch { return ''; }
}

// "at 3:00 PM" / "alle 15:00" / "a las 15:00" etc.
function timePreposition(lang) {
  const map = {
    it: 'alle', en: 'at', es: 'a las', de: 'um', fr: 'à', nl: 'om', pt: 'às',
    ru: 'в', pl: 'o', ro: 'la', zh: '', ja: '', hi: 'को', ko: '', ar: 'الساعة',
  };
  const v = map[lang] ?? map[lang?.split('-')[0]];
  return v !== undefined ? v : 'at';
}

function priorityLabel(priority, lang) {
  const labels = {
    it: { red: 'priorità alta', yellow: 'priorità media', green: 'priorità bassa' },
    en: { red: 'high priority', yellow: 'medium priority', green: 'low priority' },
    es: { red: 'prioridad alta', yellow: 'prioridad media', green: 'prioridad baja' },
    de: { red: 'hohe Priorität', yellow: 'mittlere Priorität', green: 'niedrige Priorität' },
    fr: { red: 'haute priorité', yellow: 'priorité moyenne', green: 'basse priorité' },
    nl: { red: 'hoge prioriteit', yellow: 'gemiddelde prioriteit', green: 'lage prioriteit' },
    pt: { red: 'alta prioridade', yellow: 'média prioridade', green: 'baixa prioridade' },
    ru: { red: 'высокий приоритет', yellow: 'средний приоритет', green: 'низкий приоритет' },
    pl: { red: 'wysoki priorytet', yellow: 'średni priorytet', green: 'niski priorytet' },
    ro: { red: 'prioritate înaltă', yellow: 'prioritate medie', green: 'prioritate scăzută' },
    zh: { red: '高优先级', yellow: '中优先级', green: '低优先级' },
    ja: { red: '優先度：高', yellow: '優先度：中', green: '優先度：低' },
    hi: { red: 'उच्च प्राथमिकता', yellow: 'मध्यम प्राथमिकता', green: 'कम प्राथमिकता' },
    ko: { red: '높은 우선순위', yellow: '중간 우선순위', green: '낮은 우선순위' },
    ar: { red: 'أولوية عالية', yellow: 'أولوية متوسطة', green: 'أولوية منخفضة' },
  };
  return labels[lang]?.[priority] || labels[lang?.split('-')[0]]?.[priority] || '';
}

// In-memory cache of the last-loaded day events, so swiping back to "Oggi"
// shows the previous data INSTANTLY (no spinner) while it refreshes in the
// background. Lives outside the component so it survives remounts; not
// persisted, so a fresh app open still fetches once.
// Shared cache lives in utils/viewCache so EventForm can invalidate it.

export default function TodayView() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const T = useT(user?.language);
  const locale = DATE_LOCALES[user?.language] || DATE_LOCALES['it'];
  const { subCalendars } = useSubCalendars();

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slowLoad, setSlowLoad] = useState(false);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0); // one silent retry — mobile signal drops, not a sleeping server
  const [activeSubCal, setActiveSubCal] = useState(null); // null = Tutti
  // Everything still to come under the picked label, shown right under the
  // day's own events (see renderLabelUpcoming).
  const [labelUpcoming, setLabelUpcoming] = useState(null);
  const [labelLoading, setLabelLoading] = useState(false);
  // Groups
  const [userGroups,      setUserGroups]      = useState([]);
  const [activeGroupId,   setActiveGroupId]   = useState(null); // null = solo io
  const [groupEvents,     setGroupEvents]     = useState([]);
  const [groupLoading,    setGroupLoading]    = useState(false);
  const [groupFailed,     setGroupFailed]     = useState(false); // read failed ≠ empty day
  const [groupRetry,      setGroupRetry]      = useState(0);     // bumped by the Retry button
  const [groupCounts,     setGroupCounts]     = useState({}); // { groupId: eventCount } for the selected day
  const [isSpeaking, setIsSpeaking] = useState(false);
  // What other people have shared with me for the selected day, all groups.
  const [sharedEvents, setSharedEvents] = useState([]);
  // Set only when the read-aloud genuinely could not happen. A button that does
  // nothing and says nothing is the failure this whole path exists to remove.
  const [ttsErr, setTtsErr] = useState('');
  // Holds the server-provided audio while it plays, so Stop can stop it.
  const audioRef = useRef(null);
  // Bumped by every start and every stop, so a reply that arrives after you
  // changed your mind can tell that it is no longer wanted.
  const speakTokenRef = useRef(0);
  const [view, setView] = useState('day'); // 'day' | 'week' | 'month' | 'year'
  // Week layout: 'list' (agenda) or 'grid' (hourly slots — the old Plan tab)
  const [weekMode, setWeekModeState] = useState(() => localStorage.getItem('memo_week_mode') || 'list');
  const setWeekMode = (m) => {
    setWeekModeState(m);
    try { localStorage.setItem('memo_week_mode', m); } catch (e) {}
  };
  // Day layout: same List ⇄ Grid choice for the single-day screen
  const [dayMode, setDayModeState] = useState(() => localStorage.getItem('memo_day_mode') || 'list');
  const setDayMode = (m) => {
    setDayModeState(m);
    try { localStorage.setItem('memo_day_mode', m); } catch (e) {}
  };
  const { weather, weatherLoading, permissionState, retryWeather } = useWeather(user?.language === 'en-US');
  const baseLang = (user?.language || 'it').split('-')[0];

  // NOTE: the reminder alarm (scheduler + ringing toast) now lives in the
  // app-wide <AlarmManager> component so it fires on ANY screen, not only here.

  const selectedDateObj = parseISO(selectedDate);
  const isToday = fnsIsToday(selectedDateObj);
  const isTomorrow = fnsIsTomorrow(selectedDateObj);
  const isYesterday = fnsIsToday(addDays(selectedDateObj, 1));

  // Keep sessionStorage in sync so EventForm can default to the selected date
  useEffect(() => {
    sessionStorage.setItem('memo_selected_date', selectedDate);
  }, [selectedDate]);

  // NOTE: changing day no longer drops the label filter. It used to, on every
  // one of these, so following "Palestra" across the week meant re-tapping the
  // label at every single arrow — and if a day had nothing under it you could
  // not tell whether the filter had been cleared or the day was simply empty.
  function prevDay() {
    setSelectedDate(d => format(subDays(parseISO(d), 1), 'yyyy-MM-dd'));
    setRetryCount(0);
  }
  function nextDay() {
    setSelectedDate(d => format(addDays(parseISO(d), 1), 'yyyy-MM-dd'));
    setRetryCount(0);
  }
  function goToday() {
    setSelectedDate(todayStr);
    setRetryCount(0);
  }

  function handleSelectDate(dateStr) {
    setSelectedDate(dateStr);
    setView('day');
    setRetryCount(0);
  }

  function handleMonthSelectDate(dateStr) {
    setSelectedDate(dateStr);
    setRetryCount(0);
  }

  // ── Sideways drag = another day ───────────────────────────────────────────
  // Jodi: "in Day view, to go to the previous day or next day, I have to tap on
  // the arrow. It would be a lot easier if I could just swipe the screen
  // whichever direction I want to go."
  //
  // Deliberately NOT a live drag that follows the finger: the day view is a
  // scrolling list, and a handler that grabs the touch to move the page is the
  // one that makes the list feel stuck. This only looks at where the finger
  // LANDED — no preventDefault, so scrolling up and down stays exactly as
  // native as before. Three tests have to pass together:
  //   far enough (60px, above the wobble of a tap),
  //   mostly sideways (nearly twice as much across as down, so a diagonal
  //     scroll is never mistaken for a day),
  //   and quick (under 0.8s — a slow drag is somebody reading, not flicking).
  // Buttons, links and fields are left alone: the arrows and the event cards
  // must still take an ordinary tap.
  useEffect(() => {
    if (view !== 'day') return undefined;
    claimSwipe('day');
    let x0 = 0, y0 = 0, t0 = 0, live = false;
    function start(e) {
      const t = e.touches && e.touches[0];
      if (!t) { live = false; return; }
      if (e.target?.closest?.('button, a, input, textarea, select, [role="button"]')) { live = false; return; }
      x0 = t.clientX; y0 = t.clientY; t0 = e.timeStamp; live = true;
    }
    function end(e) {
      if (!live) return;
      live = false;
      const t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - x0, dy = t.clientY - y0;
      if (e.timeStamp - t0 > 800) return;
      if (Math.abs(dx) < 60) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.8) return;
      // Dragging the page to the right uncovers what came before it. In Arabic
      // the page runs the other way, so the meaning of the direction does too.
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const back = rtl ? dx < 0 : dx > 0;
      if (back) prevDay(); else nextDay();
    }
    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchend', end, { passive: true });
    return () => {
      releaseSwipe('day');
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchend', end);
    };
  }, [view]);   // prevDay/nextDay are stable declarations in this component

  // Cancel speech on unmount
  useEffect(() => {
    return () => { window.speechSynthesis?.cancel(); };
  }, []);

  // The server no longer sleeps (paid instance), so a load that drags past 6 s
  // means the user's connection is slow — say that, don't blame the server.
  useEffect(() => {
    if (!loading) { setSlowLoad(false); return; }
    const t = setTimeout(() => setSlowLoad(true), 6000);
    return () => clearTimeout(t);
  }, [loading]);

  // Fetch user's groups once on mount
  useEffect(() => {
    apiClient.get('/groups')
      .then(r => setUserGroups(r.data || []))
      // Keep whatever we had: replacing it with nothing made the shared-calendar
      // row disappear, which reads as "you don't have any".
      .catch(() => {});
  }, []);

  // Everything other people have shared with me for this day, from every group
  // at once. Merged into Today below.
  //
  // Without this, an event a family member added was only visible after tapping
  // that group's chip — so the one thing a shared calendar is FOR, finding out
  // what somebody else put in your day, required you to already suspect it.
  //
  // Never blanks what it has: a failed refresh must not look like "nobody
  // shared anything today".
  useEffect(() => {
    if (!userGroups.length) { setSharedEvents([]); return; }
    let cancelled = false;
    apiClient.get('/groups/events', { params: { date: selectedDate } })
      .then(r => { if (!cancelled) setSharedEvents(r.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedDate, userGroups]);

  // Fetch each group's event count for the selected day, so we can show a
  // superscript badge next to every group label (SILVIA / CASA PIERI …).
  useEffect(() => {
    if (userGroups.length === 0) { setGroupCounts({}); return; }
    let cancelled = false;
    // One request for every group. It used to be one request PER group, which
    // meant the badges alone cost as many round trips as you have shared
    // calendars — and each of those is a trip to the server and back.
    apiClient.get('/groups/event-counts', { params: { date: selectedDate } })
      .then(r => { if (!cancelled) setGroupCounts(r.data || {}); })
      .catch(() => { if (!cancelled) setGroupCounts({}); });
    return () => { cancelled = true; };
  }, [selectedDate, userGroups]);

  // Fetch group events only when a specific group is selected.
  // "Solo io" (activeGroupId === null) shows only own events → no group fetch needed.
  useEffect(() => {
    if (!activeGroupId || userGroups.length === 0) { setGroupEvents([]); setGroupFailed(false); return; }
    setGroupLoading(true);
    setGroupFailed(false);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    apiClient.get(`/groups/${activeGroupId}/events`, { params: { date: selectedDate, tz } })
      .then(r => { setGroupEvents(r.data || []); setGroupFailed(false); })
      // "Nothing today" is a statement someone acts on. If we could not read
      // the calendar we must say THAT, not invent an empty day for them.
      .catch(() => setGroupFailed(true))
      .finally(() => setGroupLoading(false));
  }, [activeGroupId, selectedDate, userGroups, groupRetry]);

  /**
   * Stop whatever is talking, whichever of the two is doing it.
   */
  function stopSpeaking() {
    speakTokenRef.current++;   // anything still in flight is now stale
    try { window.speechSynthesis?.cancel(); } catch (_) {}
    const a = audioRef.current;
    if (a) {
      try { a.pause(); } catch (_) {}
      if (a.src) { try { URL.revokeObjectURL(a.src); } catch (_) {} }
      audioRef.current = null;
    }
    setIsSpeaking(false);
  }

  /**
   * Read the day aloud.
   *
   * Two ways, and the browser's own comes first: it is free, instant, works
   * offline and respects the speed the user chose in Settings. Most browsers
   * have one.
   *
   * Firefox on Android does not, and MeMo used to answer that by hiding the
   * button — so on that browser the feature did not exist, with nothing on
   * screen to say why. For a feature that is how somebody who cannot see the
   * screen learns what their day holds, that is not an acceptable answer. When
   * the browser cannot speak, the server reads it instead.
   */
  async function handleSpeak() {
    if (isSpeaking) { stopSpeaking(); return; }
    setTtsErr('');

    const lang = user?.language || 'it';
    // The SAME clock the cards are drawn with: this device's. Everything the
    // user sees works that way — the day's events are fetched for this
    // device's timezone, and a card formats the time without specifying one,
    // which means locally. Reading the account's saved timezone here instead
    // made the voice announce a different hour from the one on screen for
    // anyone whose account timezone isn't where they are (an account set to
    // the US, read on a phone in Italy, said "7 AM" for a 1:30 PM event).
    // users.timezone stays what it is for: the server's morning summary.
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const speechLang = langToSpeechLang(lang);

    // The SAME list the screen is showing, in the same order.
    //
    // It used to read `events` — the user's own calendar only — while the
    // screen draws `filteredEvents`, which is that plus everything shared with
    // them. Silvia: "se in un giorno ho 4 eventi la voce mi legge solo quelli
    // del mio calendario". Two lists, one drawn and one spoken, and nothing
    // holding them together; so now the voice reads what the eye reads, sorted
    // by time exactly as the cards are.
    const eventsToRead = (activeGroupId ? groupEvents : filteredEvents)
      .slice()
      .sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0));

    if (eventsToRead.length === 0) {
      speakText(T.today.ttsNoEvents, speechLang);
      return;
    }

    const introText = T.today.ttsIntro.replace('{n}', eventsToRead.length);
    const prep = timePreposition(lang);
    // The hour and the name, and nothing else. The priority used to be read
    // out after every single event — "alta", "bassa", "bassa", "media" — and on
    // the screen a priority is a coloured bar nobody reads aloud to themselves.
    // Silvia: "così come appaiono sullo schermo e senza menzionare etichette o
    // calendari". An icon at the front of a title is not spoken either.
    const fullText = introText + '. ' + eventsToRead.map(ev => {
      const time = ev.start ? formatEventTime(ev.start, timezone, lang) : '';
      const name = stripIcon(ev.title);
      return `${time ? prep + ' ' + time + ', ' : ''}${name}.`;
    }).join(' ');

    speakText(fullText, speechLang);
  }

  /**
   * The browser if it can, the server if it cannot. Never silence.
   */
  async function speakText(text, speechLang) {
    setIsSpeaking(true);

    if (window.speechSynthesis) {
      await voicesReady();   // or the first read of the day uses a different voice
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = speechLang;
      applyTTSPrefs(utterance); // voice + pitch + user-chosen speed
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
      return;
    }

    // No engine on this browser — ask the server for the audio.
    //
    // That request takes a moment, and Stop can be pressed during it. Without
    // the token below, stopping while it was still coming did nothing: the
    // audio arrived a second later and started talking, over a button that had
    // just said it had stopped.
    const token = ++speakTokenRef.current;
    try {
      const res = await apiClient.post('/speak',
        { text, language: user?.language || 'it' },
        { responseType: 'blob' });
      if (token !== speakTokenRef.current) return;   // stopped, or asked again
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); audioRef.current = null; setIsSpeaking(false); };
      audio.onerror = () => { setIsSpeaking(false); setTtsErr(TTS_FAIL[baseLang] || TTS_FAIL.en); };
      await audio.play();
    } catch (_) {
      // Say so. The alternative is a button that does nothing, which is what
      // this whole change exists to stop.
      setIsSpeaking(false);
      setTtsErr(TTS_FAIL[baseLang] || TTS_FAIL.en);
    }
  }

  useEffect(() => {
    let cancelled = false;
    const cacheKey = isToday ? 'today' : `d:${selectedDate}`;
    const cached = todayEventsCache.get(cacheKey);
    if (cached) {
      // Show the previous data instantly; refresh silently below.
      setEvents(cached);
      setLoading(false);
      setError(false);
    } else {
      setLoading(true);
      setError(false);
      setEvents([]);
    }

    const params = { tz: Intl.DateTimeFormat().resolvedOptions().timeZone };
    if (!isToday) params.date = selectedDate;

    apiClient.get('/events/today', { params })
      .then((res) => {
        if (!cancelled) {
          todayEventsCache.set(cacheKey, res.data);
          setEvents(res.data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (retryCount === 0) {
          setTimeout(() => {
            if (!cancelled) setRetryCount(1);
          }, 2500);
        } else {
          setError(true);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [retryCount, selectedDate]);

  // Pick a label → load everything still to come under it, to continue the
  // day's list below. Only on today: on another day "coming up" would be
  // ambiguous. Cleared as soon as the filter is dropped.
  useEffect(() => {
    if (!activeSubCal || !isToday) { setLabelUpcoming(null); setLabelLoading(false); return; }
    let cancelled = false;
    // Show what we already have while the answer comes back — this list is the
    // slowest read in the app (a whole year from the provider).
    const cached = labelAgendaCache.get(activeSubCal);
    if (cached) setLabelUpcoming(cached.events || []);
    setLabelLoading(!cached);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    apiClient.get('/events/upcoming', { params: { subCalendarId: activeSubCal, tz, days: 365 } })
      .then((res) => {
        if (cancelled) return;
        // Stored for the full label page, which the user opens next by tapping
        // the label on any of these cards — it will not have to ask again.
        if (res.data) labelAgendaCache.set(activeSubCal, res.data);
        setLabelUpcoming(res.data?.events || []);
        setLabelLoading(false);
      })
      .catch(() => { if (!cancelled) { if (!cached) setLabelUpcoming([]); setLabelLoading(false); } });
    return () => { cancelled = true; };
  }, [activeSubCal, isToday]);

  // Date label for header — always just weekday + day + month (no Today/Tomorrow/Yesterday/year)
  // The year, short: "lunedì 24 agosto 26". Two digits, no apostrophe — it was
  // a habit that only reads as one in some languages, and Silvia's rule holds:
  // if it cannot be done the same way in all sixteen, it is not done.
  // Two spaces before the year, and they have to be NON-BREAKING ones: HTML
  // collapses ordinary runs of whitespace to a single space, so "agosto  26"
  // typed with two would have come out with one and looked like nothing had
  // been done.
  // Two spaces between the day and the month as well, not just before the
  // year. Non-breaking ones throughout: HTML collapses ordinary runs of
  // whitespace to a single space, so the gaps would simply not appear.
  // The weekday with a capital letter. Italian, Spanish and others write it
  // lower case in running text, but this is a heading — and it is the first
  // word on the screen.
  const cap = (w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w);
  const dateLabel = cap(format(selectedDateObj, 'EEEE', { locale }))
    + '  ' + format(selectedDateObj, 'd', { locale })
    + '\u00A0\u00A0' + format(selectedDateObj, 'MMMM', { locale })
    + '\u00A0\u00A0' + format(selectedDateObj, 'yy', { locale });

  // Client-side filter
  /**
   * The day as it actually is: my own events, plus what other people have
   * shared with me, in time order.
   *
   * Two guards worth naming.
   *
   * Deduplicated by id: an event I created and shared is in MY calendar already
   * and would otherwise appear twice, once as mine and once as the group's.
   * Mine wins, because it carries everything the shared copy does not — the
   * reminders, the label, the attachments.
   *
   * A LABEL filter hides shared events. Labels are personal; somebody else's
   * event does not have mine, so while filtering by "Gym" it must not be in the
   * list — the filter would be lying about what it is showing.
   */
  const dayEventsAll = (() => {
    if (!sharedEvents.length) return events;
    const mine = new Set(events.map(e => e.id));
    const extra = sharedEvents.filter(e => !mine.has(e.id));
    if (!extra.length) return events;
    const startOf = e => String(e.start || '');
    return [...events, ...extra].sort((a, b) => startOf(a).localeCompare(startOf(b)));
  })();

  const filteredEvents = activeSubCal
    ? events.filter(e => e.subCalendar?.id === activeSubCal)
    : dayEventsAll;

  // Round PLAN button (Silvia's design): the speaker's twin — round, green,
  // white icon. It sits at the END of the sub-calendar row (lower than the
  // speaker, so the two can't be mis-tapped). Opens the hourly planning grid
  // of the current section (day or week); while the planner is open the same
  // button shows a list icon to go back.
  function renderPlanBtn(active, onToggle) {
    return (
      <button
        className="tts-btn"
        onClick={onToggle}
        title={T.today.viewSlots}
        aria-label={T.today.viewSlots}
        style={{ marginInlineStart: 'auto' }}
      >
        {active ? <ListIcon /> : <PlannerIcon />}
      </button>
    );
  }

  // Sub-calendar filter chips — shared by the Day and Week views.
  // showCounts uses the loaded day events, so it's day-view only.
  // planBtn (optional) is appended at the end of the row, right-aligned;
  // hideChips keeps only the button (e.g. day view with a group selected).
  // The row of label chips that used to sit under the calendars is gone, at
  // Silvia's request. It was a second row of coloured words saying what the
  // events below already say — and every label was on screen whether or not
  // anything today belonged to it. The name now lives on the event itself,
  // beside the thing it describes, and tapping it still opens that label's own
  // agenda. One row less at the top of every day.
  //
  // The function stays because the PLAN button lives at the end of this row.
  function renderSubCalBar(showCounts, planBtn = null, hideChips = false) {
    if (!planBtn) return null;
    return (
      <div className="subcal-filter-bar" style={{ alignItems: 'center', justifyContent: 'flex-end' }}>
        {planBtn}
      </div>
    );
  }

  // Picking a label asks a different question from "what's on today": it asks
  // "when do I have this?". So the day's own events stay on top, marked as
  // today, and everything still to come under that label simply continues
  // below — same cards, same type, one scroll. No second page to go to.
  function renderLabelUpcoming() {
    if (!activeSubCal || activeGroupId || !isToday) return null;
    const baseL = (user?.language || 'it').split('-')[0];
    const L = {
      it: { next: 'Prossimi', none: 'Nessun altro impegno in programma.' },
      en: { next: 'Coming up', none: 'Nothing else coming up.' },
      es: { next: 'Próximos', none: 'No hay nada más previsto.' },
      de: { next: 'Als Nächstes', none: 'Nichts weiter geplant.' },
      fr: { next: 'À venir', none: 'Rien d’autre de prévu.' },
      nl: { next: 'Binnenkort', none: 'Verder niets gepland.' },
      pt: { next: 'A seguir', none: 'Nada mais previsto.' },
      ru: { next: 'Далее', none: 'Больше ничего не запланировано.' },
      pl: { next: 'Wkrótce', none: 'Nic więcej nie zaplanowano.' },
      ro: { next: 'Urmează', none: 'Nimic altceva programat.' },
      zh: { next: '接下来', none: '没有其他安排。' },
      ja: { next: 'この先の予定', none: 'ほかに予定はありません。' },
      hi: { next: 'आगे', none: 'और कुछ निर्धारित नहीं है।' },
      ko: { next: '다음 일정', none: '예정된 일정이 더 없습니다.' },
      ar: { next: 'التالي', none: 'لا شيء آخر مقرّر.' },
    }[baseL] || { next: 'Coming up', none: 'Nothing else coming up.' };

    const todayKey = new Date().toLocaleDateString('en-CA');
    // The day for grouping. An all-day event arrives as a plain 'YYYY-MM-DD':
    // running that through new Date() reads it as UTC midnight, which lands on
    // the day before for anyone west of Greenwich. Take the date as written.
    const dayKeyOf = (ev) => {
      const s = String(ev.start || '');
      if (/^\d{4}-\d{2}-\d{2}/.test(s) && ev.allDay) return s.slice(0, 10);
      try { return new Date(s).toLocaleDateString('en-CA'); } catch (_) { return ''; }
    };

    // "Coming up" means AFTER today. This used to drop only the events whose
    // start day was today, so a holiday running 1–20 August — one long all-day
    // event, already listed above as today's — came back here a second time,
    // filed under "1 August". It read like a past event wrongly listed as
    // upcoming; it was really today's event, shown twice.
    const rest = (labelUpcoming || []).filter(ev => {
      const k = dayKeyOf(ev);
      return k ? k > todayKey : true;
    });

    if (labelLoading && !labelUpcoming) {
      return <div className="spinner" style={{ margin: '14px auto' }} />;
    }
    if (!labelUpcoming) return null;

    // Group by day so the list reads as a small agenda instead of a wall.
    const days = [];
    rest.forEach(ev => {
      const key = dayKeyOf(ev);
      const last = days[days.length - 1];
      if (last && last.key === key) last.items.push(ev); else days.push({ key, items: [ev] });
    });

    const lang = user?.language || 'it';
    const dayTitle = (key) => {
      try {
        return new Date(key + 'T12:00:00').toLocaleDateString(lang, { weekday: 'long', day: 'numeric', month: 'long' });
      } catch (_) { return key; }
    };

    return (
      <div style={{ marginTop: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{
            fontSize: '0.74rem', fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--color-text-muted)',
          }}>{L.next}</span>
          <span style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
        </div>
        {days.length === 0 && (
          <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', paddingInlineStart: 4 }}>{L.none}</p>
        )}
        {days.map(d => (
          <div key={d.key} style={{ marginBottom: 12 }}>
            <div style={{
              fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-text-muted)',
              marginBottom: 5, paddingInlineStart: 4,
            }}>{dayTitle(d.key)}</div>
            {d.items.map(ev => (
              <EventCard
                key={ev.id}
                event={ev}
                onDelete={(id) => setLabelUpcoming(prev => (prev || []).filter(e => e.id !== id))}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Perla del giorno — first thing you see, once a day, dismissible.
          It gets HOW MANY events today has (never their content) so a packed
          day can be met with something calm. null until the day has loaded. */}
      <PearlCard todayCount={isToday && !loading && !error ? events.length : null} />

      {/* Day / Week / Month / Year toggle (the old fifth "Plan" tab is now
          the Grid mode inside Week — one place for every week layout) */}
      <div className="view-toggle">
        {['day','week','month','year'].map(v => (
          <button
            key={v}
            className={`view-toggle-btn${view === v ? ' active' : ''}`}
            onClick={() => setView(v)}
          >
            {v === 'day'   ? T.today.viewDay
            : v === 'week'  ? T.today.viewWeek
            : v === 'month' ? T.today.viewMonth
            : T.today.viewYear}
          </button>
        ))}
      </div>

      {view === 'week' && (
        <>
          {weekMode === 'list'
            ? <WeekView
                weather={weather}
                activeSubCal={activeSubCal}
                planBtn={renderPlanBtn(false, () => setWeekMode('grid'))}
              />
            : <>
                {renderSubCalBar(false, renderPlanBtn(true, () => setWeekMode('list')))}
                <SlotGridView initialDate={selectedDate} activeSubCal={activeSubCal} />
              </>}
        </>
      )}
      {view === 'month' && <MonthView
        onSelectDate={handleMonthSelectDate}
        selectedDate={selectedDate}
        locale={locale}
        events={events}
        loading={loading}
        onGoToDay={() => setView('day')}
      />}
      {view === 'year'  && <YearView  onSelectDate={handleSelectDate} selectedDate={selectedDate} locale={locale} />}

      {/* Day view content */}
      {view === 'day' && (
        <>
          <div className="today-header">
            {/* No arrows: a sideways drag changes the day, and it is the
                gesture every calendar uses. The two buttons were a whole row's
                worth of width spent on something the screen already does.

                They are still reachable without seeing them — the two hidden
                buttons below do the same job and are the only way a screen
                reader can move between days, since TalkBack takes the swipe
                for itself. */}
            {/* Back to today. Taking the arrows away made the drag the only way
                to move, and a drag only ever moves you ONE day — so from a
                Tuesday three weeks out, getting home was twenty-one drags. This
                is the way home, and it only exists when you are not already
                there. */}
            <div className="date-nav" style={{ position: 'relative' }}>
              <button className="visually-hidden" onClick={prevDay}>{T.today.prevDay}</button>
              {!isToday && (
                <button
                  type="button"
                  onClick={goToday}
                  aria-label={T.nav.today}
                  title={T.nav.today}
                  style={{
                    position: 'absolute', insetInlineStart: 0, top: '50%', transform: 'translateY(-50%)',
                    width: 32, height: 32, borderRadius: '50%', border: 'none',
                    background: 'var(--color-primary)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', padding: 0, zIndex: 2,
                  }}
                >
                  <span className="rtl-flip" style={{ display: 'flex' }}>
                    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
                  </span>
                </button>
              )}
              {/* Centred again, now that the row holds only the date: the
                  controls moved to a line of their own, so there is nothing
                  left to make it drift. The padding balances the "back to
                  today" circle so the date sits in the middle of the screen and
                  not in the middle of what is left over. */}
              <div className="date-nav-center" style={{ paddingInlineStart: isToday ? 0 : 32, paddingInlineEnd: isToday ? 0 : 32, minWidth: 0 }}>
                <div className="today-date">{dateLabel}</div>
              </div>
              <button className="visually-hidden" onClick={nextDay}>{T.today.nextDay}</button>
            </div>
          </div>

        {/* Weather, planner and speaker on a line of their own, aligned to
            the left. The single row was worth trying and it did not hold: a
            date and three controls cannot share the width of a phone without
            the date being cut. */}
        {(() => {
            const dayW = (() => {
              if (permissionState !== 'granted' || !weather) return null;
              const forecastDay = weather.forecast?.[selectedDate];
              if (isToday) {
                return {
                  emoji: forecastDay?.emoji ?? weather.current?.emoji ?? '🌡',
                  temp:  weather.current?.temp ?? forecastDay?.temp ?? null,
                };
              }
              return forecastDay ?? null;
            })();
            const dayAppointments = activeGroupId ? groupEvents : filteredEvents;
            // No `length > 0` here on purpose. Hiding the button on an empty
            // day left the already-translated "you have nothing on" line
            // unreachable — and "what do I have today?" is exactly the question
            // someone who cannot see the screen needs answered.
            // Always available now: the browser reads it, or the server does.
            const hasTts = true;
            const showRetry = permissionState === 'granted' && !dayW && !weatherLoading;
            // The planner is always there, so this row always has something in
            // it now — but the guard stays honest about the rest.
            if (!dayW && !weatherLoading && !hasTts && !showRetry && !isToday) return null;
            return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '2px 0 4px' }}>
                {dayW ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{dayW.emoji}</span>
                    {dayW.temp != null && (
                      <span style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--color-text)' }}>
                        {dayW.temp}°{user?.language === 'en-US' ? 'F' : 'C'}
                      </span>
                    )}
                  </div>
                ) : weatherLoading ? (
                  <span style={{ fontSize: '1.25rem', lineHeight: 1, opacity: 0.4 }}>🌤</span>
                ) : showRetry ? (
                  <button onClick={retryWeather} aria-label={T.settings?.weatherReset || 'Meteo'} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: 'var(--color-text-muted)',
                  }}>
                    <span style={{ fontSize: '1.15rem', lineHeight: 1, opacity: 0.5 }}>🌤</span>
                    <span style={{ fontSize: '0.8rem' }}>↺</span>
                  </button>
                ) : null}
                {/* The planner in front of the speaker, both after the
                    weather: three things on one short line instead of two rows. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* After the weather, before the planner: the door to the
                      labels and the shared calendars, which until now could
                      only be reached by tapping one on an event — impossible
                      on exactly the empty day you most want to ask. */}
                  <LabelsMenu />
                  {renderPlanBtn(dayMode === 'grid', () => setDayMode(dayMode === 'grid' ? 'list' : 'grid'))}
                  {hasTts && (
                    <button
                      className={`tts-btn${isSpeaking ? ' tts-btn--stop' : ''}`}
                      onClick={handleSpeak}
                      title={isSpeaking ? T.today.ttsStop : T.today.ttsButton}
                      aria-label={isSpeaking ? T.today.ttsStop : T.today.ttsButton}
                    >
                      {isSpeaking ? <StopIcon /> : <SpeakerIcon />}
                    </button>
                  )}
                </div>
              </div>
            );
        })()}




          {/* MeMo cannot see every calendar on this account, and says so
              rather than showing two thirds of the week as if it were all of
              it. See CalendarsLimited.jsx. */}
          <CalendarsLimited />

          {/* Said out loud, not swallowed. role="alert" so a screen reader —
              the very person most likely to be waiting on this button — hears
              that it failed instead of waiting for a voice that never comes. */}
          {ttsErr && (
            <div role="alert" style={{
              fontSize: '0.78rem', color: 'var(--color-red, #C0392B)',
              padding: '0 0 6px', lineHeight: 1.4,
            }}>
              {ttsErr}
            </div>
          )}

          {/* ── Group selector ── shown only if user belongs to at least one group */}
          {userGroups.length > 0 && (
            <div className="subcal-filter-bar" style={{ borderBottom: '1px solid var(--color-border)', paddingTop: 2, paddingBottom: 10, marginBottom: 8 }}>
              {/* User's name = "Solo io" equivalent — resets both group and subcal filters */}
              <button
                className={`subcal-filter-btn filter-all-btn${activeGroupId === null ? ' active' : ''}`}
                onClick={() => { setActiveGroupId(null); setActiveSubCal(null); }}
                style={{ textTransform: 'uppercase' }}
              >
                {(user?.name || '').split(' ')[0] || T.today.filterMine || 'Solo io'}
                {events.length > 0 && ` (${events.length})`}
              </button>
              {userGroups.map(g => (
                <button
                  key={g.id}
                  className={`subcal-filter-btn${activeGroupId === g.id ? ' active' : ''}`}
                  onClick={() => setActiveGroupId(activeGroupId === g.id ? null : g.id)}
                  style={{ textTransform: 'uppercase' }}
                >
                  {g.name}
                  {groupCounts[g.id] > 0 && ` (${groupCounts[g.id]})`}
                </button>
              ))}
            </div>
          )}

          {/* Sub-calendar filter row (chips hidden while a group is selected)
              + the round PLAN button at the end of the row */}
          {/* The row that held the planner is gone: the button moved up beside
              the speaker, and the labels it used to sit with moved onto the
              events themselves. */}

          {loading && (
            <>
              <div className="spinner" />
              {slowLoad && (
                <p style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--color-text-muted)', marginTop: 8 }}>
                  {T.today.slowConnection}
                </p>
              )}
            </>
          )}

          {error && (
            <div className="alert alert-error" style={{ textAlign: 'center' }}>
              <div>{T.today.error}</div>
              <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-outline btn-sm" onClick={() => setRetryCount(r => r + 1)}>
                  {T.today.retry}
                </button>
              </div>
              {/* Signing out used to sit right next to Retry as if it were the
                  other half of the same remedy. It is the opposite: if the day
                  failed to load because there is no signal, signing out means
                  you cannot get back IN either — a passing problem turned into
                  a locked door. It is still one line down, in small grey text,
                  for the rare case where the session really is the trouble. */}
              <div style={{ marginTop: 10, fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                {T.today.signOutHint}{' '}
                <button
                  className="btn-link"
                  onClick={logout}
                  style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--color-text-muted)', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {T.settings.signOut}
                </button>
              </div>
            </div>
          )}

          {/* Grid mode: hourly slots for the selected day (respects filters) */}
          {dayMode === 'grid' && !loading && !error && (
            <SlotGridView
              day={selectedDate}
              dayEvents={activeGroupId ? groupEvents : filteredEvents}
              activeSubCal={null /* dayEvents are already filtered above */}
            />
          )}

          {dayMode === 'list' && (() => {
            // ── Display logic ────────────────────────────────────────────
            // No group selected  → show ONLY your own events (true "Solo io")
            // Group selected     → show ONLY the shared events for that group
            //                      (your shared entries + partner entries, no duplicates)
            // Note the second branch: Today now also holds events OTHER PEOPLE
            // shared, and those must not be dressed as yours. An "own" card is
            // a link to the edit screen with a delete button — for an event
            // living in somebody else's calendar, tapping it would fetch an id
            // you cannot see and answer "not found", and deleting it could
            // never work. It gets the read-only shared card instead, the same
            // one the group view uses, showing whose event it is.
            const slotOf = e => (e.isGroupEvent && !e.isOwnEvent ? 'group' : 'own');
            const merged = activeGroupId
              ? groupEvents
                  .map(e => ({ ...e, _slot: 'group' }))
                  .sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0))
              : filteredEvents
                  .map(e => ({ ...e, _slot: slotOf(e) }))
                  .sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0));

            // A shared calendar we could not read is NOT an empty day. Saying
            // "nothing on" about somebody else's day is a statement the reader
            // acts on — and it would be a made-up one.
            const isEmpty = !loading && !groupLoading && !error && !groupFailed && merged.length === 0;
            // With a label picked, the day's own events are marked in the app's
            // colour: they're the answer to "do I have this today?", and what
            // follows below answers "when is the next one?".
            const markToday = !!activeSubCal && !activeGroupId && isToday && merged.length > 0;

            return (
              <>
                {isEmpty && (
                  <div className="empty-state">
                    <span className="empty-state-icon">🌿</span>
                    <span className="empty-state-title">{isToday ? T.today.emptyTitle : T.today.emptyTitleDay}</span>
                    <span className="empty-state-sub">{T.today.emptySub}</span>
                  </div>
                )}
                {groupLoading && activeGroupId && (
                  <div className="spinner" style={{ margin: '12px auto' }} />
                )}
                {groupFailed && !groupLoading && activeGroupId && (
                  <div className="alert alert-error" style={{ textAlign: 'center' }}>
                    <div>{T.today.error}</div>
                    <button
                      className="btn btn-outline btn-sm"
                      style={{ marginTop: 8 }}
                      onClick={() => setGroupRetry(n => n + 1)}
                    >
                      {T.today.retry}
                    </button>
                  </div>
                )}
                <div className={markToday ? 'day-now' : undefined}>
                {merged.map((event) => {
                  if (event._slot === 'group') {
                    // Shared-calendar card — read-only, shows who the event belongs to
                    const m = event.member || {};
                    const isMe = event.isOwnEvent;
                    const timeStr = event.start
                      ? new Date(event.start).toLocaleTimeString(
                          user?.language || 'it',
                          { hour: '2-digit', minute: '2-digit', hour12: String(user?.language || '').startsWith('en') }
                        )
                      : '';
                    const cardInner = (
                      <>
                        <div className="event-card-top">
                          {/* Written, not initialled. A coloured circle with an M
                              in it asks you to remember that M is Mirko — and it
                              asked it on the right-hand edge, away from the time
                              it belonged to. Silvia: "indica il nome del
                              calendario e poi - e il nome di chi lo ha
                              condiviso senza altri loghi". So it reads
                              CANOVA - Mirko Pieri, under the hour, in the same
                              column as every other event's label. */}
                          <div className="event-when">
                            {timeStr && <span className="event-time">{timeStr}</span>}
                          </div>
                          <div className="event-card-main">
                            {iconOf(event.title) &&
                              <EventIcon icon={iconOf(event.title)} size={26} style={{ marginInlineEnd: 6 }} />}
                            <span className="event-title" translate="no">{stripIcon(event.title)}</span>
                          </div>
                        </div>
                        {/* The calendar's name, in the colour of whoever put the
                            event there — and nothing else. Silvia: "fino a
                            richiesta diversa non importa chi inserisce un
                            evento, i membri di un calendario sono pochi". The
                            colour already says which of the three or four it
                            was; the name said it a second time.

                            On its own line, like the labels on an ordinary
                            event: inside the fixed hour column a calendar
                            called "Casa Pieri" came out as "Casa Pie…", and a
                            name you cannot finish reading is not a name. */}
                        {event.sharedGroupName && (
                          <span className="event-label-row">
                            {event.groupId ? (
                              <Link
                                to={`/shared/${event.groupId}`}
                                className="event-label"
                                title={event.sharedGroupName}
                                style={{ color: m.color || 'var(--color-text-muted)' }}
                              >
                                {event.sharedGroupName}
                              </Link>
                            ) : (
                              <span className="event-label" style={{ color: m.color || 'var(--color-text-muted)' }}>
                                {event.sharedGroupName}
                              </span>
                            )}
                          </span>
                        )}
                        {event.location && (
                          <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: 3, paddingInlineStart: 2 }}>
                            {event.location}
                          </div>
                        )}
                        {/* The name used to be repeated here, a line below the
                            badge that already showed its initial. It is in the
                            column above now, spelled out. */}
                      </>
                    );
                    // Events you SHARED (your own) are openable/editable; events shared TO you are read-only
                    return isMe ? (
                      <Link
                        key={`g-${event.id}-${m.name}`}
                        to={`/edit/${event.id}`}
                        state={{ event }}
                        className={`event-card priority-${event.priority || 'green'}`}
                        style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                      >
                        {cardInner}
                      </Link>
                    ) : (
                      <div
                        key={`g-${event.id}-${m.name}`}
                        className={`event-card priority-${event.priority || 'green'}`}
                        style={{ opacity: 0.88 }}
                      >
                        {cardInner}
                      </div>
                    );
                  }
                  // Own event (no group selected) → standard full card
                  return (
                    <EventCard
                      key={event.id}
                      event={event}
                      onDelete={(id) => setEvents((prev) => prev.filter((e) => e.id !== id))}
                    />
                  );
                })}
                </div>
                {renderLabelUpcoming()}
              </>
            );
          })()}
        </>
      )}

    </>
  );
}
